/**
 * @file useUpdatePublications.js
 * @module src/js/hooks/useUpdatePublications
 * @description
 * **A hook to update publication entries by calling the backend API.**
 * - Utilizes React contexts to manage modal messages.
 * - Returns a function that:
 *   - Sends a PUT request to update publication data.
 *   - Updates the frontend state with the new publications list.
 *   - Utilizes modal messages to indicate the update status.
 *
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useUpdatePublications hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useModal } from "../contexts/ModalProvider";
import useFetchFromBackend from "./useFetchFromBackend";

export default function useUpdatePublications({
    setData,
    setUpdating,
    setUpdatedID, // setUpdatedID is optional (uploading: not needed)
    setShowUploadForm,
}) {
    const { pathname } = useLocation();
    const { showModalMessage } = useModal();
    const fetchFromBackend = useFetchFromBackend();

    if (typeof setData !== "function" ||
        typeof setUpdating !== "function" ||
        typeof setShowUploadForm !== "function"
    ) {
        throw new Error("setData, setUpdating, and setShowUploadForm functions are required.");
    }

    const updatePublications = useCallback(async ({ body }) => {
        if (body == null || body.id == null) {
            throw new Error("body with id must be provided to updatePublication.");
        }
        if (typeof setUpdatedID !== "function") {
            throw new Error("setUpdatedID must be provided when updating an existing publication.");
        }
        
        try {
            setUpdating(true);
            showModalMessage({ type: "info", content: "Updating..." });

            const data = await fetchFromBackend({
                request: "/api/publications",
                method: "PUT",
                body,
                requireAuth: true,
                from: pathname,
            });

            if (data && data.success) {
                const data = await fetchFromBackend({ request: `/api/publications/${body.type}` });
                setData(data || []);
                setShowUploadForm(false);
                setUpdatedID(-1);
                showModalMessage({ type: "success", content: "Successfully updated!" });

            } else if (data && data.error) {
                console.error("Failed to update:", data.error);
                showModalMessage({ type: "error", content: "Failed to update. Please try again." });
            }

        } catch (error) {
            console.error("An error occurred:", error);
            showModalMessage({ type: "error", content: "An error occurred. Please try again." });

        } finally {
            setUpdating(false);
        }

    }, [fetchFromBackend, pathname, setData, setUpdating, setUpdatedID, setShowUploadForm, showModalMessage]);

    return updatePublications;
}